import { type FieldValues } from 'react-hook-form';

import { formInitialValues } from '../components/Items/constants';

import {
  type IAdditionalFieldConfig,
  type IFeatureFlagsResponse,
  type IItemResponse,
  type ILicenseTypeResponse,
  type ILpcoResponse,
} from '../types/response';
import {
  ArrayFields,
  ConsignmentTabFields,
  HeaderTabFields,
  ItemsTabFields,
  NamesAndPartiesTabFields,
} from '../types/fieldEnums';
import { type IAdditionalField, type IDBOptionAdditionalField } from '../types/request';
import { FieldPrefix } from '../constants/fieldPrefixes';
import { DataType, Tab } from '../types/enums';

import createMapByProperty from '../../../helpers/createMapByProperty';
import { getAttachmentValuesFromConfigs } from './getFormValues';
import getSelectValue from './getSelectValue';

export function getFormValuesFromParentLicense({
  parentLpco,
  licenseType,
  licenseTypeFieldConfigs,
  parentLicenseType,
}: {
  parentLpco: ILpcoResponse;
  licenseType: ILicenseTypeResponse;
  licenseTypeFieldConfigs: Record<string, { hidden: boolean }>;
  parentLicenseType: ILicenseTypeResponse;
}) {
  const {
    licenseTypeCode,
    licenseTypeName,
    additionalFields: additionalFieldConfig,
    featureFlags,
    attachedDocuments: attachedDocumentConfig,
  } = licenseType;

  const { additionalFields: parentAdditionalFieldConfig } = parentLicenseType;

  const { invoiceValueManagementEnabled } = featureFlags;

  const {
    id,
    countryOfDestinationCode,
    countryOfDestinationName,
    typeOfUse,
    termsOfDeliveryCode,
    termsOfDeliveryDescription,
    invoiceCurrencyCode,
    invoiceExchangeRate,
    placeOfLoadingCode,
    placeOfLoadingName,
    placeOfUnloadingCode,
    placeOfUnloadingName,
    companyCode,
    companyDescription,
    items,
    attachedDocuments,
    additionalFields: parentAdditionalFields,
    dbOptionAdditionalFields: parentDbOptionAdditionalFields,
    departmentOfficeCode,
    departmentOfficeName,
    customsOfficeCode,
    customsOfficeName,
    transitCountryCode,
    transitCountryName,
    meanOfTransportCode,
    meanOfTransportName,
    // excluded fields start
    userReferenceNumber,
    requestNumber,
    requestDate,
    approvalReference,
    approvalDate,
    validFrom,
    validTo,
    beneficiaries,
    fees,
    version,
    // excluded fields end
    ...rest
  } = parentLpco;

  const { additionalFields, dbOptionAdditionalFields } = getAdditionalFieldValuesFromParentLicense({
    additionalFieldConfig,
    parentAdditionalFieldConfig,
    parentAdditionalFields,
    parentDbOptionAdditionalFields,
    tabs: [Tab.HEADER, Tab.NAMES_AND_PARTIES, Tab.CONSIGNMENT],
  });

  return {
    ...rest,
    itemTabEnabled: featureFlags.itemTabEnabled,
    [HeaderTabFields.licenseType]: getSelectValue({
      value: licenseTypeCode,
      label: licenseTypeName,
      tagLabel: licenseTypeCode,
    }),
    [HeaderTabFields.countryOfDestinationCode]:
      licenseTypeFieldConfigs[HeaderTabFields.countryOfDestinationCode]?.hidden === false
        ? getSelectValue({
            value: countryOfDestinationCode,
            tagLabel: countryOfDestinationName,
          })
        : null,
    [HeaderTabFields.customsOfficeCode]:
      licenseTypeFieldConfigs[HeaderTabFields.customsOfficeCode]?.hidden === false
        ? getSelectValue({
            value: customsOfficeCode,
            tagLabel: customsOfficeName,
          })
        : null,
    [HeaderTabFields.departmentOfficeCode]:
      licenseTypeFieldConfigs[HeaderTabFields.departmentOfficeCode]?.hidden === false
        ? getSelectValue({
            value: departmentOfficeCode,
            tagLabel: departmentOfficeName,
          })
        : null,
    [HeaderTabFields.typeOfUse]:
      licenseTypeFieldConfigs[HeaderTabFields.typeOfUse]?.hidden === false
        ? getSelectValue({
            value: typeOfUse,
          })
        : null,
    [HeaderTabFields.termsOfDeliveryCode]:
      licenseTypeFieldConfigs[HeaderTabFields.termsOfDeliveryCode]?.hidden === false
        ? getSelectValue({
            value: termsOfDeliveryCode,
            tagLabel: termsOfDeliveryDescription,
          })
        : null,
    [HeaderTabFields.invoiceCurrencyCode]: invoiceValueManagementEnabled
      ? getSelectValue({
          value: invoiceCurrencyCode,
          tagLabel: String(invoiceExchangeRate),
        })
      : null,
    [HeaderTabFields.placeOfLoadingCode]:
      licenseTypeFieldConfigs[HeaderTabFields.placeOfLoadingCode]?.hidden === false
        ? getSelectValue({
            value: placeOfLoadingCode,
            tagLabel: placeOfLoadingName,
          })
        : null,
    [HeaderTabFields.placeOfUnloadingCode]:
      licenseTypeFieldConfigs[HeaderTabFields.placeOfUnloadingCode]?.hidden === false
        ? getSelectValue({
            value: placeOfUnloadingCode,
            tagLabel: placeOfUnloadingName,
          })
        : null,

    [NamesAndPartiesTabFields.companyCode]: getSelectValue({
      value: companyCode,
      tagLabel: companyDescription,
    }),

    [ConsignmentTabFields.transitCountryCode]: getSelectValue({
      value: transitCountryCode,
      tagLabel: transitCountryName,
    }),

    [ConsignmentTabFields.meanOfTransportCode]: getSelectValue({
      value: meanOfTransportCode,
      tagLabel: meanOfTransportName,
    }),

    [ArrayFields.items]:
      items.length === 1
        ? items.map((item) =>
            getItemValuesFromParent({
              item,
              additionalFieldConfig,
              parentAdditionalFieldConfig,
              featureFlags,
            })
          )
        : [],
    [ArrayFields.attachedDocuments]: getAttachmentValuesFromConfigs(attachedDocumentConfig),
    additionalFields,
    dbOptionAdditionalFields,
    parentLpcoId: id,
  };
}

export function getAdditionalFieldValuesFromParentLicense({
  additionalFieldConfig,
  parentAdditionalFieldConfig,
  parentAdditionalFields,
  parentDbOptionAdditionalFields,
  tabs,
}: {
  additionalFieldConfig: IAdditionalFieldConfig[];
  parentAdditionalFieldConfig: IAdditionalFieldConfig[];
  parentAdditionalFields: IAdditionalField[];
  parentDbOptionAdditionalFields: IDBOptionAdditionalField[];
  tabs: Tab[];
}) {
  const parentAdditionalFieldsMapByName = createMapByProperty(parentAdditionalFieldConfig, 'englishLabel');

  const { additionalFields, dbOptionAdditionalFields } = additionalFieldConfig.reduce(
    (acc, currentConfig) => {
      const { id, dataType, tab, englishLabel } = currentConfig;

      if (!tabs.includes(tab)) {
        return acc;
      }

      const parentAdditionalFieldConfig = parentAdditionalFieldsMapByName[englishLabel];

      const key = `${FieldPrefix.additionalField}${id}`;

      if (
        !parentAdditionalFieldConfig ||
        !tabs.includes(parentAdditionalFieldConfig.tab) ||
        parentAdditionalFieldConfig.dataType !== dataType
      ) {
        if (dataType === DataType.OPTIONS_FROM_DATABASE) {
          acc.dbOptionAdditionalFields[key] = null;
        } else {
          acc.additionalFields[key] = null;
        }

        return acc;
      }

      if (dataType === DataType.OPTIONS_FROM_DATABASE) {
        const field = parentDbOptionAdditionalFields.find((item) => item.fieldId === parentAdditionalFieldConfig.id);
        const { fieldCode, optionId, fieldValue1, fieldValue2 } = field ?? {};
        acc.dbOptionAdditionalFields[key] = optionId
          ? { value: optionId, label: fieldCode, name: fieldValue1, name2: fieldValue2 }
          : null;

        return acc;
      }

      const value =
        parentAdditionalFields.find((item) => item.fieldId === parentAdditionalFieldConfig.id)?.fieldValue ?? null;

      if (dataType === DataType.LIST || dataType === DataType.MULTI_SELECT_LIST) {
        acc.additionalFields[key] = value ? { value, label: value } : null;
      } else if (dataType === DataType.CHECKBOX) {
        acc.additionalFields[key] = value === 'true' || value === true;
      } else if (dataType === DataType.DATE) {
        acc.additionalFields[key] = value ? new Date(value as string) : null;
      } else {
        acc.additionalFields[key] = value;
      }

      return acc;
    },
    {
      additionalFields: {} as FieldValues,
      dbOptionAdditionalFields: {} as FieldValues,
    }
  );

  return {
    additionalFields,
    dbOptionAdditionalFields,
  };
}

export function getItemValuesFromParent({
  item,
  additionalFieldConfig,
  parentAdditionalFieldConfig,
  featureFlags,
}: {
  item: IItemResponse;
  additionalFieldConfig: IAdditionalFieldConfig[];
  parentAdditionalFieldConfig: IAdditionalFieldConfig[];
  featureFlags: IFeatureFlagsResponse;
}) {
  const {
    requestedAndApprovedAmountEnabled,
    packageManagementEnabled,
    invoiceValueManagementEnabled,
    itemWeightManagementEnabled,
    treatmentTableEnabled,
  } = featureFlags;

  const {
    id,
    packageTypeName,
    requestedUomName,
    requestedUomName2,
    approvedUomName,
    approvedUomName2,
    countryOfOriginName,
    approvedAmount,
    approvedAmount2,
    approvedUomCode,
    approvedUomCode2,
    remainingAmount,
    remainingAmount2,
    packages,
    treatments,
    commodityCode,
    countryOfOriginCode,
    packageTypeCode,
    requestedUomCode,
    requestedUomCode2,
    packageMark,
    packageNumber,
    grossMass,
    netMass,
    itemInvoiceAmountInNationalCurrency,
    requestedAmount,
    requestedAmount2,
    additionalFields: parentAdditionalFields,
    dbOptionAdditionalFields: parentDbOptionAdditionalFields,
    ...rest
  } = item;

  const { additionalFields, dbOptionAdditionalFields } = getAdditionalFieldValuesFromParentLicense({
    additionalFieldConfig,
    parentAdditionalFieldConfig,
    parentAdditionalFields,
    parentDbOptionAdditionalFields,
    tabs: [Tab.ITEM],
  });

  return {
    ...formInitialValues,
    ...rest,
    [ItemsTabFields.parentItemId]: id as number,
    [ItemsTabFields.commodityCode]: getSelectValue({ value: commodityCode, description: rest.commodityDescription }),
    [ItemsTabFields.countryOfOriginCode]: getSelectValue({
      value: countryOfOriginCode,
      tagLabel: countryOfOriginName,
    }),
    [ItemsTabFields.packageTypeCode]: packageManagementEnabled
      ? getSelectValue({
          value: packageTypeCode,
          tagLabel: packageTypeName,
        })
      : null,
    [ItemsTabFields.packageNumber]: packageManagementEnabled ? packageNumber : null,
    [ItemsTabFields.packageMark]: packageManagementEnabled ? packageMark : null,
    [ItemsTabFields.requestedUomCode]: requestedAndApprovedAmountEnabled
      ? getSelectValue({
          value: requestedUomCode,
          description: requestedUomName,
        })
      : null,
    [ItemsTabFields.requestedUomCode2]: requestedAndApprovedAmountEnabled
      ? getSelectValue({
          value: requestedUomCode2,
          description: requestedUomName2,
        })
      : null,
    [ItemsTabFields.grossMass]: itemWeightManagementEnabled ? grossMass : null,
    [ItemsTabFields.netMass]: itemWeightManagementEnabled ? netMass : null,
    [ItemsTabFields.requestedAmount]: requestedAndApprovedAmountEnabled ? requestedAmount : null,
    [ItemsTabFields.requestedAmount2]: requestedAndApprovedAmountEnabled ? requestedAmount2 : null,
    [ItemsTabFields.itemInvoiceAmountInNationalCurrency]: invoiceValueManagementEnabled
      ? itemInvoiceAmountInNationalCurrency
      : null,
    [ItemsTabFields.packages]: packageManagementEnabled ? packages.map(({ id, ...rest }) => rest) : [],
    [ItemsTabFields.treatments]: treatmentTableEnabled ? treatments.map(({ id, ...rest }) => rest) : [],
    additionalFields,
    dbOptionAdditionalFields,
  };
}
